import Vue from 'vue'
import VueRouter, { RouteConfig, NavigationGuard } from 'vue-router'
import Home from '../views/Home.vue'
import store from '@/store'
import { authInit } from '@/store/modules/auth'

import EmptyRouterView from './EmptyRouterView.vue'

interface Redirect {
  route: string;
  check(): Promise<boolean>;
}

const redirects = (redirects: Array<Redirect>) => {
  const guard: NavigationGuard<Vue> = async (to, from, next) => {
    for (const redirect of redirects) {
      if (false == await redirect.check()) {
        next({name: redirect.route})
        return
      }
    }
    next()
  }
  return guard
}

const requiresAuth: Redirect = {
  check: async () => {
    await authInit;
    return store.getters.isLoggedIn
  },
  route: 'AuthStart'
}

const requiresVerification: Redirect = {
  check: async () => {
    await authInit;
    return store.getters.isVerified
  },
  route: 'EmailVerification'
}

const requiresProfile: Redirect = {
  check: async () => {
    await authInit;
    await store.dispatch('bindProfile')
    
    return store.getters.hasProfile
  },
  route: 'Profile'
}
 
Vue.use(VueRouter)

  const routes: Array<RouteConfig> = [
  {
    path: '/',
    name: 'Home',
    component: Home
  },
  {
    path: '/content',
    component: () => import(/* webpackChunkName: "content" */ "@/views/content/Content.vue"),
    beforeEnter: redirects([
      requiresAuth,
      requiresVerification
    ]),
    children: [
      {
        path: 'profile',
        name: 'Profile',
        component: () => import(/* webpackChunkName: "content" */ '@/views/content/Profile.vue'),
      },
      {
        path: 'info/guide',
        name: 'guide',
        component: () => import(/* webpackChunkName: "info" */ "@/views/info/Guide.vue"),
      },
      {
        path: 'info/privacypolicy',
        name: 'privacypolicy',
        component: () => import(/* webpackChunkName: "info" */ "@/views/info/PrivacyPolicy.vue"),
      },
      {
        path: 'info/credits',
        name: 'credits',
        component: () => import(/* webpackChunkName: "info" */ "@/views/info/Credits.vue"),
      },
      {
        path: '',
        name: 'Overview',
        component: () => import(/* webpackChunkName: "content" */ '@/views/content/Overview.vue'),
        beforeEnter: redirects([
          requiresProfile,
        ])
      },
      {
        path: 'surveys',
        component: EmptyRouterView,
        beforeEnter: redirects([
          requiresProfile,
        ]),
        children: [
          {
            path: '',
            name: 'SurveyList',
            component: () => import(/* webpackChunkName: "content" */ '@/views/content/SurveyList.vue'),
          },
          {
            path: ':id',
            name: 'Survey',
            component: () => import(/* webpackChunkName: "content" */ '@/views/content/Survey.vue'),
          }
        ]
      },
      {
        path: 'maps',
        component: EmptyRouterView,
        beforeEnter: redirects([
          requiresProfile,
        ]),
        children: [
          {
            path: '',
            name: 'Maps',
            component: () => import(/* webpackChunkName: "content" */ "@/views/content/MapsList.vue")
          },
          {
            path: ':id',
            name: 'MapsDetail',
            component: () => import(/* webpackChunkName: "content" */ "@/views/content/Map.vue")
          }
        ]
      },
    ]
  },
  {
    path: '/auth',
    component: EmptyRouterView,
    children: [
      {
        path: 'login',
        component: EmptyRouterView,
        children: [
          {
            path: 'recover-password',
            name: 'EmailRecover',
            component: () => import(/* webpackChunkName: "auth" */ '@/views/auth/EmailRecover.vue'),
          },
          {
            path: '',
            name: 'Login',
            component: () => import(/* webpackChunkName: "auth" */ '@/views/auth/Login.vue'),
          }
        ]
      },
      {
        path: 'register',
        component: EmptyRouterView,
        children: [
          {
            path: 'emailregister',
            name: 'EmailRegister',
            component: () => import(/* webpackChunkName: "auth" */ '@/views/auth/EmailRegister.vue'),
          },
          {
            path: '',
            name: 'Register',
            component: () => import(/* webpackChunkName: "auth" */ '@/views/auth/Register.vue'),
          }
        ]
      },
      {
        path: '',
        name: 'AuthStart',
        component: () => import(/* webpackChunkName: "auth" */ '@/views/auth/Start.vue'),
      },
    ]
  },
  {
    path: '/auth/emailverification',
    name: 'EmailVerification',
    component: () => import(/* webpackChunkName: "auth" */ '@/views/auth/EmailVerification.vue'),
    beforeEnter: redirects([
      requiresAuth
    ])
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})


export default router
